import { Church, Users, Shield, Siren, Microscope, Rocket, Leaf, type LucideIcon } from "lucide-react";

export type PillarKey =
  | "churches"
  | "women_and_children"
  | "veterans"
  | "law_enforcement"
  | "cancer_research"
  | "defense_space_force"
  | "green_environment";

export type PillarDef = {
  key: PillarKey;
  label: string;
  tagline: string;
  description: string;
  icon: LucideIcon;
  quickStarts: string[];
};

export const DEFAULT_QUICK_STARTS: string[] = [
  "How do the 7 pillars work?",
  "Help me set a monthly savings goal.",
  "Explain the 5% revenue routing.",
];

export const PILLARS: PillarDef[] = [
  {
    key: "churches",
    label: "Churches",
    tagline: "Faith & community",
    description: "Support local congregations and faith-based outreach programs.",
    icon: Church,
    quickStarts: [
      "How can I tithe consistently on a tight budget?",
      "Suggest a giving plan for my parish.",
      "Which budgeting pillar funds church outreach?",
    ],
  },
  {
    key: "women_and_children",
    label: "Women & Children",
    tagline: "Protect the vulnerable",
    description: "Fund shelters, mentorship, and care for women and children in need.",
    icon: Users,
    quickStarts: [
      "How do I budget for childcare expenses?",
      "Suggest charities supporting single mothers.",
      "Help me plan a family emergency fund.",
    ],
  },
  {
    key: "veterans",
    label: "Veterans",
    tagline: "Honor those who served",
    description: "Fund mental health, housing, and reintegration for U.S. veterans.",
    icon: Shield,
    quickStarts: [
      "How can VA benefits fit my budget?",
      "Suggest veteran-owned businesses to support.",
      "Help me set a giving goal for veteran charities.",
    ],
  },
  {
    key: "law_enforcement",
    label: "Law Enforcement",
    tagline: "Back the badge",
    description: "Support officers, their families, and community safety programs.",
    icon: Siren,
    quickStarts: [
      "How do shift-work paychecks fit a budget?",
      "Suggest funds for officers' families.",
      "Plan a monthly contribution to a police charity.",
    ],
  },
  {
    key: "cancer_research",
    label: "Cancer Research",
    tagline: "Cure the future",
    description: "Accelerate breakthrough oncology research and patient support.",
    icon: Microscope,
    quickStarts: [
      "How can I budget for medical co-pays?",
      "Suggest research orgs to support monthly.",
      "Help me set a giving goal for cancer research.",
    ],
  },
  {
    key: "defense_space_force",
    label: "Defense & Space Force",
    tagline: "Guard the frontier",
    description: "Support service members defending the nation on land, sea, and orbit.",
    icon: Rocket,
    quickStarts: [
      "How do deployment cycles affect budgeting?",
      "Suggest charities for service families.",
      "Plan a giving cadence aligned with pay periods.",
    ],
  },
  {
    key: "green_environment",
    label: "Green Environment",
    tagline: "Protect the planet",
    description: "Fund conservation, clean energy, and reforestation efforts.",
    icon: Leaf,
    quickStarts: [
      "How do I lower household energy spend?",
      "Suggest reforestation orgs to support.",
      "Plan a monthly green-giving goal.",
    ],
  },
];

export const pillarLabel = (key?: string | null) =>
  PILLARS.find((p) => p.key === key)?.label ?? "Not selected";

export const getQuickStarts = (key?: string | null): string[] =>
  PILLARS.find((p) => p.key === key)?.quickStarts ?? DEFAULT_QUICK_STARTS;

